<template>
  <div class="server-selection">
    <div class="server-selection__header">
      <h2 class="server-selection__title">Выбор сервера</h2>
      <p class="server-selection__subtitle">
        Выберите сервер для подключения
      </p>
    </div>

    <div class="server-selection__content">
      <div v-if="serverStore.isLoading" class="server-selection__loading">
        <Loader />
        <p>Загружаем список серверов...</p>
      </div>

      <div v-else-if="serverStore.error" class="server-selection__error">
        <p>Ошибка загрузки серверов: {{ serverStore.error }}</p>
        <BaseButton 
          text="Повторить"
          styles="secondary-200"
          @on-submit="serverStore.fetchServers"
        />
      </div>

      <div v-else-if="serverStore.servers.length === 0" class="server-selection__empty">
        <p>Нет доступных серверов</p>
      </div>

      <div v-else class="server-selection__servers">
        <div 
          v-for="server in serverStore.servers"
          :key="server.id"
          :class="[
            'server-selection__server-card',
            {
              'server-selection__server-card--selected': localSelectedServer?.id === server.id
            }
          ]"
          @click="selectServerLocally(server)"
        >
          <div class="server-selection__server-info">
            <h3 class="server-selection__server-name">{{ server.name }}</h3>
            <p class="server-selection__server-protocol">{{ getProtocolName(server.protocol) }}</p>
          </div>
          <!-- <div v-if="localSelectedServer?.id === server.id" class="server-selection__server-check">
            ✓
          </div> -->
        </div>
      </div>

      <div class="server-selection__footer">
        <BaseButton
          text="Продолжить"
          styles="primary-gradient"
          :is-disabled="!localSelectedServer"
          @on-submit="handleContinue"
        />
        <BaseButton
          text="Назад"
          styles="secondary-200"
          @on-submit="$emit('back')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useServerStore } from '@/entities/Server'
import type { Server } from '@/entities/Server'
import BaseButton from '@/ui/components/Base/BaseButton.vue'
import Loader from '@/ui/components/Loader.vue'

const emit = defineEmits<{
  'server-selected': [server: Server]
  'back': []
}>()

const serverStore = useServerStore()
// Локальный выбранный сервер (не сохраняется в store до нажатия "Продолжить")
const localSelectedServer = ref<Server | null>(null)

onMounted(async () => {
  if (serverStore.servers.length === 0) {
    await serverStore.fetchServers()
  }
})

function selectServerLocally(server: Server) {
  localSelectedServer.value = server
  console.log('Локально выбран сервер:', server)
}

function handleContinue() {
  if (localSelectedServer.value) {
    console.log('Подтверждаем выбор сервера:', localSelectedServer.value)
    emit('server-selected', localSelectedServer.value)
  }
}

function getProtocolName(protocol: string): string {
  switch (protocol) {
    case 'xray':
      return 'X-Ray'
    case 'wireguard':
      return 'WireGuard'
    case 'outline':
      return 'Outline'
    default:
      return protocol.toUpperCase()
  }
}
</script>

<style lang="scss" scoped>
.server-selection {
  // padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  // padding-bottom: 60px;
  // min-height: 100vh;
  height: 100%;
  // padding: 40px 20px;
  width: 100%;

  &__header {
    text-align: center;
    margin-bottom: 40px;
  }

  &__title {
    @include h1;
    color: $neutral-100;
    margin: 0 0 16px 0;
  }

  &__subtitle {
    @include p2-regular;
    color: $neutral-60;
    margin: 0;
  }

  &__content {
    width: 100%;
    max-width: 334px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex-grow: 1;
  }

  &__loading,
  &__error,
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
    color: $neutral-100;
    padding: 40px 0;
  }

  &__servers {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__server-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    background: $secondary-200;
    border-radius: 16px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: $secondary-200-active;
    }

    &--selected {
      border-color: $primary-100;
      background: $secondary-200-active;
    }
  }

  &__server-info {
    flex: 1;
  }

  &__server-name {
    @include p1-medium;
    color: $neutral-100;
    margin: 0 0 4px 0;
  }

  &__server-protocol {
    @include p3-regular;
    color: $neutral-60;
    margin: 0;
  }

  // &__server-check {
  //   color: $primary-100;
  //   font-size: 20px;
  //   font-weight: bold;
  //   margin-left: 12px;
  // }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: auto;

    .button-base-block {
      display: flex;
      height: 48px;

      :deep(button) {
        flex-grow: 1;
        justify-content: center;
      }
    }
  }
}
</style>
